import React, { FunctionComponent, useEffect, useMemo } from 'react';

import Flex from '@gojob/ui-bits/Flex';
import useIsHydrated from '@gojob/ui-bits/hooks/useIsHydrated';
import { Option } from '@gojob/ui-bits/PlacesAutocomplete/useRemoteOptions';
import Text from '@gojob/ui-bits/Text';
import { HistoryLocation } from '@reach/router';
import { parse } from 'query-string';

import InfiniteJobList from 'src/components/InfiniteJobList';
import useQueryJobsCount from 'src/components/InfiniteJobList/useQueryJobsCount';
import WithLayout from 'src/components/Layout/WithLayout';
import RelatedOffers from 'src/components/RelatedOffers';
import IndicatorPanel from 'src/components/SearchForm/IndicatorPanel';
import SearchFormWithNavigation from 'src/components/SearchForm/SearchFormWithNavigation';
import Seo from 'src/components/Seo';
import SignupPromotion from 'src/components/SignupPromotion';

import searchTracking from 'src/tracking/search';
import track from 'src/tracking/track';
import useTrackPageview from 'src/tracking/useTrackPageview';
import useTranslation from 'src/translations/useTranslation';
import transformJobCityToOptions from 'src/utils/transformJobCityToOptions';

export type SearchResultsProps = {
  location?: HistoryLocation;
};

export type ParsedSearchQuery = {
  q?: string;
  city?: Option;
  radius?: number;
};

export type SearchQuery = {
  q?: string;
  lat?: string;
  lng?: string;
  ville?: string;
  radius?: string;
};

export const useParsedQuery = (search = ''): ParsedSearchQuery => {
  const parameters: SearchQuery = parse(search);

  const { q, ville } = parameters;

  const parsedRadius = Number(parameters.radius);
  const radius = isNaN(parsedRadius) ? undefined : parsedRadius;

  const city = useMemo(() => transformJobCityToOptions(ville, parameters?.lat, parameters?.lng), [
    parameters.lat,
    parameters.lng,
    ville,
  ]);

  return { q, city, radius };
};

const SearchResults: FunctionComponent<SearchResultsProps> = (props) => {
  const { location } = props;

  const t = useTranslation();
  const isHydrated = useIsHydrated();

  const { q, city, radius } = useParsedQuery(location?.search);
  const [lng, lat] = city?.centre?.coordinates || [];

  /**
   * Update 11/05/20: Implement tracking for this runtime query
   * More details here - https://gitlab.com/gojob/board/backend/-/issues/35
   */
  const { count, loading, error } = useQueryJobsCount(q, lat, lng, radius, 'JOB_OFFERS');
  useEffect(() => {
    if (!loading && !error && count === 0) {
      searchTracking.noResultAfterSearch();
    }
    if (!loading && !error && typeof count !== 'undefined') {
      track('results', { pageNumber: 0, results: count });
    }
  }, [count, loading, error]);

  useTrackPageview();

  return (
    <WithLayout>
      {/** Avoid indexing every page with url params combinations */}
      <Seo noIndex />

      <Flex width="100%" flexDirection="column" alignItems="stretch" padding={[4, 0, 6]}>
        <Text variant="headline1" as="h1" gutter={2}>
          {t('find_your_next_mission')}
        </Text>

        {isHydrated ? (
          <SearchFormWithNavigation
            key={location?.key || location?.search}
            margin={[0, 0, 2]}
            defaultPlace={city}
            defaultSearchKeyword={q}
            radius={radius}
            css={{ zIndex: 1 }}
          />
        ) : (
          <SearchFormWithNavigation margin={[0, 0, 2]} css={{ zIndex: 1 }} />
        )}

        <IndicatorPanel keyword={q} city={city} radius={radius} count={count} loading={loading} />

        <Flex flexDirection="column" alignItems="stretch" margin={[2, -1]}>
          <InfiniteJobList
            key={location?.search}
            loading={loading || !isHydrated}
            rowCount={count}
            query={q}
            lat={lat}
            lng={lng}
            radius={radius}
          />

          <RelatedOffers loading={loading} keyword={q} city={city} radius={radius} count={count} />
        </Flex>

        <SignupPromotion css={{ alignSelf: 'center' }} margin={[10, 2, 2]} />
      </Flex>
    </WithLayout>
  );
};

export default SearchResults;
